import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import {
  coLearningGalleryPickerBackdropProps,
  coLearningInkMuted,
} from '../ui/coLearningModalTheme';

/**
 * ARTmosphere premium modal shell — cream blur backdrop, gold accent, serif title.
 */
export default function PremiumActivityModalShell({
  open,
  onClose,
  title,
  eyebrow = 'Magic Studio',
  subtitle = '',
  children,
  maxWidthClass = 'max-w-7xl',
  zIndexClass = 'z-[200050]',
  ariaLabelledBy,
  dataActivityDashboard,
}) {
  if (!open || typeof document === 'undefined') return null;

  const titleId = ariaLabelledBy || 'premium-activity-modal-title';

  return createPortal(
    <div
      {...coLearningGalleryPickerBackdropProps}
      className={`${coLearningGalleryPickerBackdropProps.className} ${zIndexClass}`}
      onClick={onClose}
      role="presentation"
      data-activity-dashboard={dataActivityDashboard ? 'true' : undefined}
    >
      <div
        className={`appreciation-modal-container relative flex max-h-[min(94dvh,920px)] w-full ${maxWidthClass} flex-col overflow-hidden rounded-[28px] border border-[#F6D57B]/80 bg-[rgba(255,255,255,0.94)] shadow-[0_28px_80px_rgba(120,72,20,0.18)] backdrop-blur-xl sm:rounded-[32px]`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{
          marginTop: 'max(0px, env(safe-area-inset-top, 0px))',
          maxHeight: 'calc(94dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div
          className="h-1 w-full shrink-0 bg-gradient-to-r from-[#F59E0B] via-[#FBBF24] to-[#F97316]"
          aria-hidden
        />

        <header className="relative shrink-0 border-b border-[#F5D99B]/50 bg-[#FFFDF9]/95 px-4 py-3.5 backdrop-blur-md sm:px-6 sm:py-4">
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,214,140,0.35),transparent_70%)]"
            aria-hidden
          />
          <div className="relative flex items-start justify-between gap-3">
            <div className="min-w-0">
              {eyebrow ? (
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9A7A55]">
                  {eyebrow}
                </p>
              ) : null}
              <h2
                id={titleId}
                className="font-serif text-lg font-semibold leading-tight text-[#2F251B] sm:text-xl md:text-2xl"
              >
                {title}
              </h2>
              {subtitle ? (
                <p className={`mt-0.5 truncate text-sm ${coLearningInkMuted}`}>{subtitle}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#E8C97A] bg-[#FFF9EF] text-[#5C4428] shadow-sm transition hover:border-[#F5CB5C] hover:bg-white hover:shadow-md"
              aria-label="Close"
              title="Close"
            >
              <X className="h-4 w-4" strokeWidth={2.25} />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
}
